/**
 * Purchase Detail Page
 *
 * Comprehensive view of a purchase with all information:
 * - Purchase info (provider, invoice, dates)
 * - Line items table
 * - Totals
 * - Deposits/payments
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  FileText,
  FileJson,
  Package,
  CreditCard,
  Plus,
  Loader2,
  Truck,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDate, formatDateTime, cn } from '@/lib/utils'
import { HACIENDA_STATUS_LABELS, HACIENDA_STATUS_COLORS } from '@/types/bill'
import { FriendlyErrors, formatJson } from '@/pages/settings/incidentShared'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toaster'

interface PurchaseDeposit {
  id: number
  amount: number
  notes: string | null
  created_at: string
}

interface PurchaseAttempt {
  id: number
  attempt_number: number
  status: string
  error_code: string | null
  error_message: string | null
  request_payload: string | null
  response_payload: string | null
  created_at: string | null
}

interface PurchaseDetail {
  id: number
  provider_id: number | null
  provider_name: string | null
  invoice_number: string | null
  invoice_date: string | null
  subtotal: number
  total_general_discount: number
  subtotal_2: number
  total_sale_tax: number
  transport_cost: number
  total: number
  is_paid: boolean
  is_cancelled: boolean
  hacienda_status: string | null
  hacienda_message: string | null
  clave_hacienda: string | null
  consecutivo_hacienda: string | null
  acceptance_type: string | null
  supplier_document_key: string | null
  receiver_consecutive: string | null
  notes: string | null
  created_at: string
  deposits: PurchaseDeposit[]
  total_deposited: number
  remaining_debt: number
  attempts: PurchaseAttempt[]
}

export function PurchaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const purchaseId = id && !isNaN(Number(id)) ? Number(id) : 0

  const [showDepositDialog, setShowDepositDialog] = useState(false)
  const [depositAmount, setDepositAmount] = useState('')
  const [depositNotes, setDepositNotes] = useState('')
  const [historyExpanded, setHistoryExpanded] = useState(false)
  const [expandedAttempt, setExpandedAttempt] = useState<number | null>(null)

  // Fetch purchase
  const { data: purchase, isLoading, error, refetch } = useQuery({
    queryKey: ['purchase', purchaseId],
    queryFn: async () => {
      const response = await api.get<PurchaseDetail>(`/purchases/${purchaseId}`)
      return response.data
    },
    enabled: !!purchaseId,
  })

  // Add deposit mutation
  const addDepositMutation = useMutation({
    mutationFn: async (data: { amount: number; notes: string }) => {
      const response = await api.post(`/purchases/${purchaseId}/deposits`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] })
      queryClient.invalidateQueries({ queryKey: ['purchase', purchaseId] })
      toast({
        title: t('purchases.depositAdded'),
        variant: 'success',
      })
      setShowDepositDialog(false)
      setDepositAmount('')
      setDepositNotes('')
      refetch()
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('purchases.depositError'),
        variant: 'destructive',
      })
    },
  })

  const syncHaciendaMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/purchases/${purchaseId}/sync-hacienda`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] })
      queryClient.invalidateQueries({ queryKey: ['purchase', purchaseId] })
      toast({ title: t('invoicing.statusUpdated'), variant: 'success' })
      refetch()
    },
    onError: () => {
      toast({ title: t('invoicing.syncError'), variant: 'destructive' })
    },
  })

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast({ title: `${label} ${t('common.copied')}` })
  }

  const haciendaStatusIcon = (status?: string | null) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
      case 'partial':
        return <CheckCircle2 className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
      case 'rejected':
      case 'failed':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
      default:
        return <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
    }
  }

  const handleAddDeposit = () => {
    const amount = parseFloat(depositAmount)
    if (!amount || amount <= 0) {
      toast({
        title: t('common.error'),
        description: t('purchases.invalidAmount'),
        variant: 'destructive',
      })
      return
    }
    addDepositMutation.mutate({ amount, notes: depositNotes })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (error || !purchase) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('purchases.purchaseNotFound')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {error ? t('purchases.errorLoading') : t('purchases.doesNotExist')}
        </p>
        <Button variant="outline" onClick={() => navigate('/purchases')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('purchases.backToPurchases')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/purchases')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">
            {t('purchases.purchaseOrder')} #{purchase.id}
          </h1>
          <p className="text-sm text-muted-foreground">
            {formatDate(purchase.created_at)}
          </p>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Purchase Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('purchases.purchaseInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">{t('purchases.provider')}</p>
                  <p className="font-medium">{purchase.provider_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('purchases.invoiceNumber')}</p>
                  <p className="font-medium">{purchase.invoice_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('purchases.invoiceDate')}</p>
                  <p className="font-medium">
                    {purchase.invoice_date ? formatDate(purchase.invoice_date) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('purchases.createdAt')}</p>
                  <p className="font-medium">{formatDateTime(purchase.created_at)}</p>
                </div>
                {purchase.notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">{t('purchases.notes')}</p>
                    <p className="font-medium">{purchase.notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recepción Hacienda (Mensaje Receptor) */}
          {(purchase.acceptance_type || purchase.clave_hacienda || purchase.hacienda_status) && (
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 space-y-0">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileJson className="h-4 w-4" />
                  {t('purchases.reception')}
                </CardTitle>
                {purchase.acceptance_type && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => syncHaciendaMutation.mutate()}
                    disabled={syncHaciendaMutation.isPending}
                  >
                    <RefreshCw className={cn('h-4 w-4 mr-1', syncHaciendaMutation.isPending && 'animate-spin')} />
                    {syncHaciendaMutation.isPending ? t('invoicing.syncing') : t('invoicing.syncStatus')}
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status badge */}
                <div className="flex items-center gap-2 flex-wrap">
                  {haciendaStatusIcon(purchase.hacienda_status)}
                  <Badge
                    className={
                      HACIENDA_STATUS_COLORS[purchase.hacienda_status ?? ''] ||
                      (purchase.hacienda_status === 'partial'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-gray-100 text-gray-800')
                    }
                  >
                    {purchase.hacienda_status === 'partial'
                      ? t('purchases.partialAcceptance')
                      : HACIENDA_STATUS_LABELS[purchase.hacienda_status ?? ''] ||
                        purchase.hacienda_status ||
                        t('purchases.pending')}
                  </Badge>
                  {purchase.acceptance_type && (
                    <Badge variant="outline" className="text-xs">
                      {purchase.acceptance_type === '1'
                        ? t('purchases.accepted')
                        : purchase.acceptance_type === '2'
                        ? t('purchases.partial')
                        : purchase.acceptance_type === '3'
                        ? t('purchases.rejected')
                        : purchase.acceptance_type}
                    </Badge>
                  )}
                </div>

                {/* Clave del documento del proveedor */}
                {purchase.clave_hacienda && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {t('purchases.supplierDocKey')}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono bg-muted p-2 rounded flex-1 overflow-x-auto">
                        {purchase.clave_hacienda}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => copyToClipboard(purchase.clave_hacienda!, t('bills.claveHacienda'))}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Consecutivo receptor */}
                {purchase.receiver_consecutive && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('purchases.receiverConsecutive')}</p>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-muted p-2 rounded">
                        {purchase.receiver_consecutive}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => copyToClipboard(purchase.receiver_consecutive!, t('bills.consecutivoHacienda'))}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Mensaje / respuesta de Hacienda */}
                {(() => {
                  const raw = purchase.hacienda_message || ''
                  const isError =
                    purchase.hacienda_status === 'rejected' ||
                    purchase.hacienda_status === 'failed' ||
                    purchase.hacienda_status === 'error'
                  if (isError && raw) {
                    return <FriendlyErrors errors={[]} rawMessage={raw} />
                  }
                  if (purchase.hacienda_status === 'submitted') {
                    return (
                      <div className="p-3 rounded-lg text-sm bg-muted text-muted-foreground">
                        {t('purchases.awaitingHacienda')}
                      </div>
                    )
                  }
                  if (!raw) return null
                  const isAccepted = purchase.hacienda_status === 'accepted'
                  return (
                    <div
                      className={cn(
                        'p-3 rounded-lg text-sm',
                        isAccepted
                          ? 'bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400'
                          : 'bg-yellow-50 dark:bg-yellow-950 text-yellow-600 dark:text-yellow-400'
                      )}
                    >
                      <p className="font-medium mb-1">
                        {isAccepted ? t('purchases.acceptedByHacienda') : t('purchases.haciendaResponse')}
                      </p>
                      <p className="text-xs whitespace-pre-wrap">{raw}</p>
                    </div>
                  )
                })()}

                {/* Historial de envíos (Mensaje Receptor) con JSON */}
                {purchase.attempts && purchase.attempts.length > 0 && (
                  <Collapsible open={historyExpanded} onOpenChange={setHistoryExpanded}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between h-auto py-2">
                        <span className="text-sm">
                          {t('bills.submissionHistory')} ({purchase.attempts.length})
                        </span>
                        {historyExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-2 mt-2">
                      {purchase.attempts.map((attempt) => (
                        <div key={attempt.id} className="border rounded-lg overflow-hidden">
                          <div
                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50"
                            onClick={() =>
                              setExpandedAttempt(
                                expandedAttempt === attempt.id ? null : attempt.id
                              )
                            }
                          >
                            <div className="flex items-center gap-2">
                              {haciendaStatusIcon(attempt.status)}
                              <span className="text-sm font-medium">
                                {t('bills.attempt')} #{attempt.attempt_number}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {attempt.created_at ? formatDateTime(attempt.created_at) : ''}
                              </span>
                              {expandedAttempt === attempt.id ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )}
                            </div>
                          </div>

                          {expandedAttempt === attempt.id && (
                            <div className="px-3 pb-3 space-y-2">
                              {attempt.error_message && (
                                <div className="p-2 rounded bg-red-50 dark:bg-red-950 text-xs text-red-600 dark:text-red-400">
                                  <p className="font-medium">
                                    {t('bills.error')}
                                    {attempt.error_code ? `: ${attempt.error_code}` : ''}
                                  </p>
                                  <p>{attempt.error_message}</p>
                                </div>
                              )}

                              {attempt.request_payload && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-xs font-medium">{t('bills.requestPayload')}</p>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        copyToClipboard(
                                          formatJson(attempt.request_payload!),
                                          t('bills.requestPayload')
                                        )
                                      }}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-32">
                                    {formatJson(attempt.request_payload)}
                                  </pre>
                                </div>
                              )}

                              {attempt.response_payload && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-xs font-medium">{t('bills.responsePayload')}</p>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        copyToClipboard(
                                          formatJson(attempt.response_payload!),
                                          t('bills.responsePayload')
                                        )
                                      }}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-32">
                                    {formatJson(attempt.response_payload)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Totals and Payments */}
        <div className="space-y-4">
          {/* Totals Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('purchases.totals')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchases.subtotal')}</span>
                <span>{formatCurrency(purchase.subtotal)}</span>
              </div>
              {purchase.total_general_discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('purchases.generalDiscount')}</span>
                  <span>-{formatCurrency(purchase.total_general_discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchases.tax')} (13%)</span>
                <span>{formatCurrency(purchase.total_sale_tax)}</span>
              </div>
              {purchase.transport_cost > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    {t('purchases.transport')}
                  </span>
                  <span>{formatCurrency(purchase.transport_cost)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('purchases.total')}</span>
                  <span>{formatCurrency(purchase.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Deposits/Payments Card */}
          {purchase.deposits && purchase.deposits.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  {t('purchases.payments')} ({purchase.deposits.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {purchase.deposits.map((deposit) => (
                  <div
                    key={deposit.id}
                    className="flex justify-between items-center p-2 rounded-lg bg-muted/50"
                  >
                    <div>
                      <p className="font-medium">{formatCurrency(deposit.amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(deposit.created_at)}
                        {deposit.notes && ` - ${deposit.notes}`}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Deposit Dialog */}
      <Dialog open={showDepositDialog} onOpenChange={setShowDepositDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('purchases.addPayment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">{t('purchases.total')}</span>
                <span className="font-bold">{formatCurrency(purchase.total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('purchases.remainingDebt')}</span>
                <span className="font-bold text-red-600">{formatCurrency(purchase.remaining_debt)}</span>
              </div>
            </div>

            <div>
              <Label>{t('purchases.amount')}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                max={purchase.remaining_debt}
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="mt-1"
                placeholder={formatCurrency(purchase.remaining_debt)}
              />
            </div>

            <div>
              <Label>{t('purchases.notes')}</Label>
              <Input
                value={depositNotes}
                onChange={(e) => setDepositNotes(e.target.value)}
                className="mt-1"
                placeholder={t('purchases.paymentNotes')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDepositDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAddDeposit}
              disabled={addDepositMutation.isPending}
            >
              {addDepositMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {t('purchases.addPayment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
